import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { ApiError } from "../../shared/api/client";
import type {
  Attendance,
  MeetingAttendanceInput,
  MeetingDetail,
  MeetingPointInput,
} from "../../shared/types/api";
import { Button, Card, CardBody, CardHeader, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import { describeOutstanding, outstandingForMinutes } from "./meetingVocabulary";

interface DraftPoint {
  body: string;
  actionOwnerId: string;
  actionDueDate: string;
}

function draftPointsOf(meeting: MeetingDetail): DraftPoint[] {
  return meeting.points.map((point) => ({
    body: point.body,
    actionOwnerId: point.actionOwner?.id ?? "",
    actionDueDate: point.actionDueDate ?? "",
  }));
}

/**
 * Writes up a meeting: a summary, the points in the order they came up, and who was there.
 *
 * A draft can be saved at any time. Submitting locks the minutes for good, so the button stays
 * off until nothing is missing, and says what is.
 */
export function MinutesEditor({
  meeting,
  canSaveDraft,
  onSaveDraft,
  onSubmit,
}: {
  meeting: MeetingDetail;
  /** Whether the server will accept a draft from this viewer — MEETING_MANAGE, not just canRecordMinutes. */
  canSaveDraft: boolean;
  onSaveDraft: (body: { summary: string | null; points: MeetingPointInput[] }) => Promise<void>;
  onSubmit: (body: { summary: string; points: MeetingPointInput[]; attendance: MeetingAttendanceInput[] }) => Promise<void>;
}) {
  const [summary, setSummary] = useState(meeting.minutesSummary ?? "");
  const [points, setPoints] = useState<DraftPoint[]>(() => draftPointsOf(meeting));
  const [attendance, setAttendance] = useState<Record<string, Attendance | undefined>>({});
  const [busy, setBusy] = useState<"draft" | "submit" | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Resynced when the page moves to another meeting without remounting this editor.
  useEffect(() => {
    setSummary(meeting.minutesSummary ?? "");
    setPoints(draftPointsOf(meeting));
    setAttendance({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting.id]);

  const owners = meeting.attendees.filter((a) => !a.guest && a.employeeId);
  const started = Date.parse(meeting.startsAt) <= Date.now();
  const missing = outstandingForMinutes(
    { summary, points, attendance },
    meeting.attendees.map((a) => a.id),
  );

  const cleanPoints = (): MeetingPointInput[] =>
    points
      .filter((p) => p.body.trim())
      .map((p) => ({
        body: p.body.trim(),
        actionOwnerId: p.actionOwnerId || null,
        actionDueDate: p.actionOwnerId && p.actionDueDate ? p.actionDueDate : null,
      }));

  const run = async (kind: "draft" | "submit", action: () => Promise<void>) => {
    setBusy(kind);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save the minutes.");
    } finally {
      setBusy(null);
    }
  };

  const update = (index: number, change: Partial<DraftPoint>) =>
    setPoints((current) => current.map((p, i) => (i === index ? { ...p, ...change } : p)));

  const move = (index: number, by: -1 | 1) =>
    setPoints((current) => {
      const next = [...current];
      const target = index + by;
      if (target < 0 || target >= next.length) {
        return current;
      }
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });

  return (
    <Card>
      <CardHeader
        title="Minutes"
        subtitle={missing.length === 0 ? "Ready to submit. Once submitted they cannot be changed." : describeOutstanding(missing)}
      />
      <CardBody className="space-y-5">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}
        {!started && (
          <InlineAlert tone="info">Minutes can be submitted once the meeting has started. You can draft them now.</InlineAlert>
        )}

        <Field label="Summary" required hint="What was decided, in a few sentences.">
          <Textarea rows={4} value={summary} onChange={(e) => setSummary(e.target.value)} />
        </Field>

        <div className="space-y-3">
          <p className="text-sm font-medium text-ink-900">Discussion points</p>
          {points.map((point, index) => (
            <div key={index} className="space-y-2 rounded-lg border border-ink-100 p-3">
              <div className="flex items-start gap-2">
                <span className="pt-2 text-xs text-ink-400">{index + 1}.</span>
                <Textarea
                  aria-label="Point"
                  rows={2}
                  value={point.body}
                  onChange={(e) => update(index, { body: e.target.value })}
                />
                <div className="flex flex-col">
                  <Button variant="ghost" size="sm" title="Move up" onClick={() => move(index, -1)}>
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" title="Move down" onClick={() => move(index, 1)}>
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Remove point"
                    onClick={() => setPoints((current) => current.filter((_, i) => i !== index))}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div className="ml-5 grid grid-cols-2 gap-2">
                <Select
                  aria-label="Action owner"
                  value={point.actionOwnerId}
                  onChange={(e) => update(index, { actionOwnerId: e.target.value })}
                >
                  <option value="">No action</option>
                  {owners.map((a) => (
                    <option key={a.id} value={a.employeeId!}>
                      {a.name}
                    </option>
                  ))}
                </Select>
                <Input
                  type="date"
                  aria-label="Due"
                  disabled={!point.actionOwnerId}
                  value={point.actionDueDate}
                  onChange={(e) => update(index, { actionDueDate: e.target.value })}
                />
              </div>
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setPoints((current) => [...current, { body: "", actionOwnerId: "", actionDueDate: "" }])}
          >
            Add point
          </Button>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-ink-900">Who was there</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setAttendance(Object.fromEntries(meeting.attendees.map((a) => [a.id, "PRESENT" as Attendance])))
              }
            >
              Mark everyone present
            </Button>
          </div>
          <ul className="divide-y divide-ink-100">
            {meeting.attendees.map((a) => (
              <li key={a.id} className="flex items-center justify-between py-2 text-sm">
                <span>
                  {a.name}
                  {a.guest && <span className="text-ink-400"> · guest</span>}
                </span>
                <span className="flex gap-4">
                  {(["PRESENT", "ABSENT"] as const).map((mark) => (
                    <label key={mark} className="inline-flex items-center gap-1.5">
                      <input
                        type="radio"
                        name={`attendance-${a.id}`}
                        aria-label={`${mark === "PRESENT" ? "Present" : "Absent"} — ${a.name}`}
                        checked={attendance[a.id] === mark}
                        onChange={() => setAttendance((current) => ({ ...current, [a.id]: mark }))}
                      />
                      {mark === "PRESENT" ? "Present" : "Absent"}
                    </label>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex justify-end gap-2">
          {canSaveDraft && (
            <Button
              variant="secondary"
              loading={busy === "draft"}
              onClick={() => void run("draft", () => onSaveDraft({ summary: summary.trim() || null, points: cleanPoints() }))}
            >
              Save draft
            </Button>
          )}
          <Button
            loading={busy === "submit"}
            disabled={!started || missing.length > 0}
            onClick={() =>
              void run("submit", () =>
                onSubmit({
                  summary: summary.trim(),
                  points: cleanPoints(),
                  attendance: meeting.attendees.map((a) => ({ attendeeId: a.id, attendance: attendance[a.id]! })),
                }),
              )
            }
          >
            Submit minutes
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
